import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Col,
  Navbar,
  Placeholder,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Utils from "../../configs/Utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeftLong,
  faArrowsRotate,
  faShop,
} from "@fortawesome/free-solid-svg-icons";
import CopyButton from "../../Components/Common/CopyButton";
import QuickEdit from "../../Components/Common/QuickEdit";
import Enums from "../../configs/Enums";

import "./storeDetail.scss";
import PageHeader from "../../Components/Common/PageHeader";

const StoreDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [storeDetail, setStoreDetail] = useState();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    Utils.setPageTitle("Store detail page");
  }, []);

  useEffect(() => {
    const fetchStoreDetail = async () => {
      try {
        const data = await Utils._apiRequest({
          path: Enums.endpoints.store.DETAIL,
          pathParams: {
            id: id,
          },
          type: "GET",
        });
        setStoreDetail(data);
      } catch (error) {
        console.log(error.message);
      }
    };

    fetchStoreDetail();
  }, [id]);

  const onQuickEditUpdate = (key, value) => {
    const updatedStoreDetail = { ...storeDetail, [key]: value };
    setStoreDetail(updatedStoreDetail);
  };

  const onRefreshTokenHandle = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const response = await Utils._apiRequest({
      path: Enums.endpoints.tiktok.AUTH_TOKEN,
      type: "GET",
      pathParams: {
        id: id,
      },
    });
    setStoreDetail(response);
    setIsLoading(false);
  };

  return (
    <main className="page-main page-store-detail">
      <div className="main-container container">
        <PageHeader 
          title={
            storeDetail?._id ? (
              storeDetail.storeFriendlyName || storeDetail.storeName
            ) : (
              <Placeholder animation="glow">
                <Placeholder xs={4} />
              </Placeholder>
            )
          } 
          description={
            storeDetail?._id ? (
              storeDetail.domain
            ) : (
              <Placeholder animation="glow">
                <Placeholder size="sm" xs={3} />
              </Placeholder>
            )
          } 
          icon={faShop}
        >
          <Button
            variant="dark"
            className={storeDetail?._id ? "" : "btn-placeholder"}
            size="sm"
            onClick={() => navigate("/stores")}
          >
            <FontAwesomeIcon className="me-2" icon={faArrowLeftLong} />
            Back to Stores
          </Button>
        </PageHeader>

        <Row>
          <Col md={12}>
            <div className="my-3 p-3 bg-body rounded shadow-sm feature-box">
              <h6 className="heading border-bottom pb-2 mb-0">Details</h6>
              <Table
                className="store-detail-table w-100 mx-auto border-bottom"
                style={{ tableLayout: "fixed" }}
                striped
                size="sm"
                borderless
              >
                <tbody>
                  <tr>
                    <th className="text-end pe-4">Store Name</th>
                    <td>
                      {storeDetail?.storeName ? (
                        storeDetail.storeName
                      ) : (
                        <Placeholder animation="glow">
                          <Placeholder xs={3} />
                        </Placeholder>
                      )}
                    </td>
                  </tr>
                  {storeDetail?.storeFriendlyName && (
                    <tr>
                      <th className="text-end pe-4">Store Friendly Name</th>
                      <td>
                        <QuickEdit
                          onUpdate={onQuickEditUpdate}
                          path={Enums.endpoints.store.DETAIL}
                          pathParams={{ id }}
                          param="storeFriendlyName"
                          defaultValue={storeDetail.storeFriendlyName}
                        />
                      </td>
                    </tr>
                  )}

                  {storeDetail?.flatform === "woocommerce" && (
                    <tr>
                      <th className="text-end pe-4">Store Time-Zone</th>
                      <td>
                        <QuickEdit
                          onUpdate={onQuickEditUpdate}
                          path={Enums.endpoints.store.DETAIL}
                          pathParams={{ id }}
                          param="meta.time_zone"
                          defaultValue={storeDetail?.meta?.time_zone || "+7"}
                        />
                      </td>
                    </tr>
                  )}

                  {storeDetail?.domain && (
                    <tr>
                      <th className="text-end pe-4">Domain</th>
                      <td>{storeDetail.domain}</td>
                    </tr>
                  )}

                  <tr>
                    <th className="text-end pe-4">Flatform</th>
                    <td>
                      {storeDetail?.flatform ? (
                        storeDetail.flatform
                      ) : (
                        <Placeholder animation="glow">
                          <Placeholder xs={2} />
                        </Placeholder>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th className="text-end pe-4">Status</th>
                    <td>
                      {storeDetail?.status ? (
                        Utils.buildBadgeLable(storeDetail.status)
                      ) : (
                        <Placeholder animation="glow">
                          <Placeholder xs={2} />
                        </Placeholder>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th className="text-end pe-4">Store Prefix</th>
                    <td>
                      {storeDetail?.storePrefix ? (
                        storeDetail.storePrefix
                      ) : (
                        <Placeholder animation="glow">
                          <Placeholder xs={8} />
                        </Placeholder>
                      )}
                    </td>
                  </tr>

                  {storeDetail?.storeId && (
                    <tr>
                      <th className="text-end pe-4">Store ID</th>
                      <td>{storeDetail.storeId}</td>
                    </tr>
                  )}

                  {storeDetail?.meta?.cipher && (
                    <tr>
                      <th className="text-end pe-4">Shop cipher</th>
                      <td>
                        <CopyButton textToCopy={storeDetail.meta.cipher} />
                      </td>
                    </tr>
                  )}
                  {storeDetail?.meta?.code && (
                    <tr>
                      <th className="text-end pe-4">Shop code</th>
                      <td>
                        <CopyButton textToCopy={storeDetail.meta.code} />
                      </td>
                    </tr>
                  )}
                  {storeDetail?.meta?.region && (
                    <tr>
                      <th className="text-end pe-4">Shop region</th>
                      <td>{storeDetail.meta.region}</td>
                    </tr>
                  )}
                  {storeDetail?.consumer?.access_token && (
                    <tr>
                      <th className="text-end pe-4">Access token</th>
                      <td>
                        <CopyButton
                          textToCopy={storeDetail.consumer.access_token}
                        />
                      </td>
                    </tr>
                  )}
                  {storeDetail?.consumer?.access_token_expire_in && (
                    <tr>
                      <th className="text-end pe-4">Access token expired</th>
                      <td>
                        <CopyButton
                          textToCopy={
                            storeDetail.consumer.access_token_expire_in
                          }
                          textToDisplay={Utils.dateFormat(
                            storeDetail.consumer.access_token_expire_in,
                            false,
                          )}
                        />
                      </td>
                    </tr>
                  )}
                  {storeDetail?.consumer?.refresh_token && (
                    <tr>
                      <th className="text-end pe-4">Refresh token</th>
                      <td>
                        <CopyButton
                          textToCopy={storeDetail.consumer.refresh_token}
                        />
                      </td>
                    </tr>
                  )}
                  {storeDetail?.consumer?.refresh_token_expire_in && (
                    <tr>
                      <th className="text-end pe-4">Refresh token expired</th>
                      <td>
                        <CopyButton
                          textToCopy={
                            storeDetail.consumer.refresh_token_expire_in
                          }
                          textToDisplay={Utils.dateFormat(
                            storeDetail.consumer?.refresh_token_expire_in,
                            false,
                          )}
                        />
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <div
                className={
                  storeDetail?.flatform === "tiktokshop"
                    ? "d-flex justify-content-end"
                    : "d-none"
                }
              >
                <Button
                  disabled={isLoading}
                  variant="warning"
                  onClick={onRefreshTokenHandle}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="grow" size="sm" className="me-2" />
                      <i>Updating new token</i>
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon
                        icon={faArrowsRotate}
                        size="sm"
                        className="me-2"
                      />
                      Refresh token
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </main>
  );
};

export default StoreDetailPage;
